import { z } from 'zod'

export const CRM_COMPANY_READ_CONTRACT_VERSION = '1.0.0' as const

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM company read alias. Domain validation stays
 * in the existing handlers so status/error precedence remains unchanged. */
export const crmCompanyReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  limit: optionalQueryString,
  includeArchived: optionalQueryString,
  cursor: optionalQueryString,
  companyId: optionalQueryString,
  opportunityId: optionalQueryString,
}).passthrough()

export const crmCompanyReadSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmCompanyPlatformOrgSchema = z.object({
  id: z.string().min(1),
  name: nullableString,
}).passthrough()

export const crmCompanySummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  platformOrg: crmCompanyPlatformOrgSchema.nullable(),
  createdByUserId: z.string().min(1),
  ownerUserId: nullableString,
  name: z.string(),
  domain: nullableString,
  segment: nullableString,
  tier: nullableString,
  country: nullableString,
  notes: nullableString,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmCompanyListResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  companies: z.array(crmCompanySummarySchema),
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmCompanyDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  company: crmCompanySummarySchema,
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmContactSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  companyId: z.string().min(1),
  platformUserId: nullableString,
  createdByUserId: z.string().min(1),
  fullName: z.string(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  whatsappE164: nullableString,
  title: nullableString,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmCompanyContactsListResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  contacts: z.array(crmContactSummarySchema),
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmContactDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  contact: crmContactSummarySchema,
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmCompanyFileKindSchema = z.enum(['proposal', 'timeline', 'contract', 'deck', 'other'])
export const crmCompanyFileSentViaSchema = z.enum(['email', 'whatsapp', 'manual', 'link'])
export const crmCompanyFileSentStatusSchema = z.enum(['sent', 'superseded', 'revoked'])

export const crmCompanyFileSentSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  companyId: nullableString,
  contactId: nullableString,
  opportunityId: nullableString,
  uploadId: z.string().min(1),
  kind: crmCompanyFileKindSchema,
  sentVia: crmCompanyFileSentViaSchema,
  label: nullableString,
  sentAt: z.string(),
  sentByUserId: nullableString,
  supersededById: nullableString,
  status: crmCompanyFileSentStatusSchema,
  idempotencyKey: z.string().min(1),
  interactionId: nullableString,
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmCompanyFilesSentListResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  files: z.array(crmCompanyFileSentSummarySchema),
  nextCursor: nullableString,
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

export const crmCompanyInteractionKindSchema = z.enum([
  'note', 'email', 'whatsapp', 'call', 'meeting', 'transcript', 'file_sent',
  'stage_change', 'task', 'system', 'platform_event',
])
export const crmCompanyInteractionDirectionSchema = z.enum(['in', 'out', 'internal'])

export const crmCompanyTimelineEntrySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  companyId: z.string().min(1),
  contactId: nullableString,
  leadId: nullableString,
  opportunityId: nullableString,
  kind: crmCompanyInteractionKindSchema,
  direction: crmCompanyInteractionDirectionSchema,
  occurredAt: z.string(),
  summary: z.string(),
  source: z.string(),
  sourceRef: nullableString,
  payloadRef: nullableString,
  createdByUserId: nullableString,
  createdAt: z.string(),
}).passthrough()

export const crmCompanyTimelineResponseSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  timeline: z.array(crmCompanyTimelineEntrySchema),
  nextCursor: nullableString,
  sideEffects: crmCompanyReadSideEffectsSchema,
}).passthrough()

/** Finance is intentionally a plain typed 503 body until the trainer/billing
 * read-through is live; it is not a CR_* error envelope. */
export const crmCompanyFinanceNotConfiguredSchema = z.object({
  contract_version: z.literal(CRM_COMPANY_READ_CONTRACT_VERSION),
  finance: z.object({
    status: z.literal('not_configured'),
    reason: z.literal('finance_read_through_not_configured'),
    sourceOfTruth: z.literal('trainer_billing_lanes'),
    invoices: z.array(z.unknown()),
    settlements: z.array(z.unknown()),
  }).passthrough(),
}).passthrough()

export const CRM_COMPANY_READ_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmCompanyReadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_COMPANY_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmCompanyReadQuery = z.infer<typeof crmCompanyReadQuerySchema>
export type CrmCompanyReadSideEffects = z.infer<typeof crmCompanyReadSideEffectsSchema>
export type CrmCompanyPlatformOrg = z.infer<typeof crmCompanyPlatformOrgSchema>
export type CrmCompanySummary = z.infer<typeof crmCompanySummarySchema>
export type CrmCompanyListResponse = z.infer<typeof crmCompanyListResponseSchema>
export type CrmCompanyDetailResponse = z.infer<typeof crmCompanyDetailResponseSchema>
export type CrmContactSummary = z.infer<typeof crmContactSummarySchema>
export type CrmCompanyContactsListResponse = z.infer<typeof crmCompanyContactsListResponseSchema>
export type CrmContactDetailResponse = z.infer<typeof crmContactDetailResponseSchema>
export type CrmCompanyFileKind = z.infer<typeof crmCompanyFileKindSchema>
export type CrmCompanyFileSentVia = z.infer<typeof crmCompanyFileSentViaSchema>
export type CrmCompanyFileSentStatus = z.infer<typeof crmCompanyFileSentStatusSchema>
export type CrmCompanyFileSentSummary = z.infer<typeof crmCompanyFileSentSummarySchema>
export type CrmCompanyFilesSentListResponse = z.infer<typeof crmCompanyFilesSentListResponseSchema>
export type CrmCompanyInteractionKind = z.infer<typeof crmCompanyInteractionKindSchema>
export type CrmCompanyInteractionDirection = z.infer<typeof crmCompanyInteractionDirectionSchema>
export type CrmCompanyTimelineEntry = z.infer<typeof crmCompanyTimelineEntrySchema>
export type CrmCompanyTimelineResponse = z.infer<typeof crmCompanyTimelineResponseSchema>
export type CrmCompanyFinanceNotConfigured = z.infer<typeof crmCompanyFinanceNotConfiguredSchema>
export type CrmCompanyReadErrorCode = (typeof CRM_COMPANY_READ_ERROR_CODES)[number]
export type CrmCompanyReadErrorEnvelope = z.infer<typeof crmCompanyReadErrorEnvelopeSchema>
